/**
 * Connect Space mutations.
 *
 * Mirrors ``features/share/hooks/useShareMutations`` — every mutation
 * invalidates the single Connect query root (``connectQueryKey.all``) so the
 * hub list + open detail shells re-fetch in lock-step.
 *
 * STEP-2 scope (this agent): create + patch (rename / archive / mode / caps).
 * The grant / invite / chatbot mutations are wired here as thin wrappers over
 * the existing ``@ando/core`` endpoint helpers so E2 (management forms) can
 * consume them WITHOUT re-deriving the cache-invalidation contract — the
 * authoring UI is the only thing left to build. Detail mutations also
 * invalidate the affected space's detail key.
 *
 * BE surface: ``ando-agent/src/api/v1/endpoints/connect.py``.
 */

import {
  type ConnectChatbotDisableResponse,
  type ConnectChatbotEnableResponse,
  type ConnectGrant,
  type ConnectGrantRequest,
  type ConnectInviteRequest,
  type ConnectMember,
  type ConnectSpace,
  type ConnectSpaceDetail,
  type CreateConnectSpaceRequest,
  type UpdateConnectSpaceRequest,
  addGrant,
  createSpace,
  disableChatbot,
  enableChatbot,
  inviteMember,
  patchSpace,
  revokeGrant,
  revokeMember,
} from "@ando/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { connectQueryKey } from "./useConnectSpaces.js";

function invalidateAll(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: connectQueryKey.all });
}

function invalidateDetail(qc: ReturnType<typeof useQueryClient>, spaceId: string): void {
  void qc.invalidateQueries({ queryKey: connectQueryKey.detail(spaceId) });
  void qc.invalidateQueries({ queryKey: connectQueryKey.list() });
}

export function useCreateConnectSpace() {
  const qc = useQueryClient();
  return useMutation<ConnectSpace, Error, CreateConnectSpaceRequest>({
    mutationFn: (body) => createSpace(body),
    onSuccess: () => invalidateAll(qc),
  });
}

export interface PatchConnectSpaceVars {
  readonly spaceId: string;
  readonly patch: UpdateConnectSpaceRequest;
}

export function usePatchConnectSpace() {
  const qc = useQueryClient();
  return useMutation<ConnectSpaceDetail, Error, PatchConnectSpaceVars>({
    mutationFn: ({ spaceId, patch }) => patchSpace(spaceId, patch),
    onSuccess: (_data, { spaceId }) => invalidateDetail(qc, spaceId),
  });
}

// ---------------------------------------------------------------------------
// Management mutations — consumed by E2's authoring forms. Wired here so the
// invalidation contract lives in ONE place (DRY); E2 only builds the UI.
// ---------------------------------------------------------------------------

export interface GrantVars {
  readonly spaceId: string;
  readonly grant: ConnectGrantRequest;
}

export function useAddConnectGrant() {
  const qc = useQueryClient();
  return useMutation<ConnectGrant, Error, GrantVars>({
    mutationFn: ({ spaceId, grant }) => addGrant(spaceId, grant),
    onSuccess: (_data, { spaceId }) => invalidateDetail(qc, spaceId),
  });
}

export interface RevokeGrantVars {
  readonly spaceId: string;
  readonly grantId: string;
}

export function useRevokeConnectGrant() {
  const qc = useQueryClient();
  return useMutation<void, Error, RevokeGrantVars>({
    mutationFn: ({ spaceId, grantId }) => revokeGrant(spaceId, grantId),
    onSuccess: (_data, { spaceId }) => invalidateDetail(qc, spaceId),
  });
}

export interface InviteVars {
  readonly spaceId: string;
  readonly invite: ConnectInviteRequest;
}

export function useInviteConnectMember() {
  const qc = useQueryClient();
  return useMutation<ConnectMember, Error, InviteVars>({
    mutationFn: ({ spaceId, invite }) => inviteMember(spaceId, invite),
    onSuccess: (_data, { spaceId }) => invalidateDetail(qc, spaceId),
  });
}

export interface RevokeMemberVars {
  readonly spaceId: string;
  readonly memberId: string;
}

export function useRevokeConnectMember() {
  const qc = useQueryClient();
  return useMutation<void, Error, RevokeMemberVars>({
    mutationFn: ({ spaceId, memberId }) => revokeMember(spaceId, memberId),
    onSuccess: (_data, { spaceId }) => invalidateDetail(qc, spaceId),
  });
}

export function useEnableConnectChatbot() {
  const qc = useQueryClient();
  return useMutation<ConnectChatbotEnableResponse, Error, string>({
    mutationFn: (spaceId) => enableChatbot(spaceId),
    onSuccess: (_data, spaceId) => invalidateDetail(qc, spaceId),
  });
}

export function useDisableConnectChatbot() {
  const qc = useQueryClient();
  return useMutation<ConnectChatbotDisableResponse, Error, string>({
    mutationFn: (spaceId) => disableChatbot(spaceId),
    onSuccess: (_data, spaceId) => invalidateDetail(qc, spaceId),
  });
}
